import React, { useCallback, useEffect, useState } from 'react';

import { AlertTriangle, DollarSign, Package, Wrench } from 'lucide-react';

import { get } from '../../services/apiClient';
import { Badge } from '../common/Badge';
import { Button } from '../common/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../common/Card';
import { Input } from '../forms/Input';

interface InventoryItem {
  id: number;
  name: string;
  description?: string | null;
  category?: string | null;
  asset_tag?: string | null;
  serial_number?: string | null;
  condition?: string | null;
  status?: string | null;
  room_number?: string | null;
  building?: string | null;
  purchase_price?: number | null;
  current_value?: number | null;
  warranty_expiration?: string | null;
  next_maintenance_date?: string | null;
  current_location?: { name: string } | null;
  assigned_to_user?: { full_name: string } | null;
  is_due_for_maintenance: boolean;
  needs_attention: boolean;
}

interface InventorySearchResponse {
  items: InventoryItem[];
  pagination: {
    page: number;
    page_size: number;
    total_count: number;
    total_pages: number;
  };
}

interface InventoryStats {
  total_items: number;
  items_by_category: Record<string, number>;
  items_by_condition: Record<string, number>;
  items_by_status: Record<string, number>;
  items_by_location: Record<string, number>;
  total_value: number;
  items_needing_maintenance: number;
  items_needing_attention: number;
  low_stock_items: number;
  overdue_maintenance: number;
}

const formatLabel = (value?: string | null) =>
  value
    ? value
        .split('_')
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ')
    : 'Not specified';

const getLocation = (item: InventoryItem) =>
  item.current_location?.name ||
  [item.building, item.room_number].filter(Boolean).join(' · ') ||
  'Unassigned';

const InventoryDashboard: React.FC = () => {
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [stats, setStats] = useState<InventoryStats | null>(null);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');
  const [condition, setCondition] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchInventory = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        page: '1',
        page_size: '200',
        include_related: 'true',
      });
      if (query.trim()) params.set('query', query.trim());
      if (status) params.set('status', status);
      if (condition) params.set('condition', condition);

      const [itemsResponse, statsResponse] = await Promise.all([
        get<InventorySearchResponse>(`/inventory/items/search?${params}`),
        get<InventoryStats>('/inventory/stats'),
      ]);
      setInventory(itemsResponse.items);
      setStats(statsResponse);
    } catch (err) {
      console.error('Failed to load inventory:', err);
      setInventory([]);
      setStats(null);
      setError(
        err instanceof Error ? err.message : 'Inventory could not be loaded.'
      );
    } finally {
      setLoading(false);
    }
  }, [condition, query, status]);

  useEffect(() => {
    const timer = window.setTimeout(() => void fetchInventory(), 250);
    return () => window.clearTimeout(timer);
  }, [fetchInventory]);

  const summaryCards = [
    {
      label: 'Total Assets',
      value: (stats?.total_items ?? 0).toLocaleString(),
      icon: Package,
    },
    {
      label: 'Current Value',
      value: new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
      }).format(stats?.total_value ?? 0),
      icon: DollarSign,
    },
    {
      label: 'Maintenance Due',
      value: (stats?.items_needing_maintenance ?? 0).toLocaleString(),
      icon: Wrench,
    },
    {
      label: 'Needs Attention',
      value: (stats?.items_needing_attention ?? 0).toLocaleString(),
      icon: AlertTriangle,
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          Inventory Management
        </h1>
        <p className="mt-1 text-gray-600">
          District assets, assignments, condition, and maintenance status.
        </p>
      </div>

      {error && (
        <Card className="border border-red-200 bg-red-50 p-5">
          <p className="font-medium text-red-800">Inventory unavailable</p>
          <p className="mt-1 text-sm text-red-700">{error}</p>
          <Button
            variant="outline"
            className="mt-3"
            onClick={() => void fetchInventory()}
          >
            Try again
          </Button>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {summaryCards.map(({ label, value, icon: Icon }) => (
          <Card key={label}>
            <CardContent className="flex items-center justify-between p-5">
              <div>
                <p className="text-sm font-medium text-gray-600">{label}</p>
                <p className="mt-1 text-2xl font-bold text-gray-900">{value}</p>
              </div>
              <Icon className="h-7 w-7 text-blue-600" />
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Asset Search</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <Input
              value={query}
              onChange={event => setQuery(event.target.value)}
              placeholder="Search name, asset tag, serial number..."
              aria-label="Search inventory"
            />
            <select
              value={status}
              onChange={event => setStatus(event.target.value)}
              className="rounded-md border border-gray-300 px-3 py-2"
              aria-label="Filter inventory by status"
            >
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="in_use">In use</option>
              <option value="available">Available</option>
              <option value="reserved">Reserved</option>
              <option value="maintenance">Maintenance</option>
              <option value="lost">Lost</option>
              <option value="stolen">Stolen</option>
              <option value="retired">Retired</option>
            </select>
            <select
              value={condition}
              onChange={event => setCondition(event.target.value)}
              className="rounded-md border border-gray-300 px-3 py-2"
              aria-label="Filter inventory by condition"
            >
              <option value="">All conditions</option>
              <option value="excellent">Excellent</option>
              <option value="good">Good</option>
              <option value="fair">Fair</option>
              <option value="poor">Poor</option>
              <option value="needs_repair">Needs repair</option>
              <option value="out_of_order">Out of order</option>
              <option value="retired">Retired</option>
              <option value="disposed">Disposed</option>
            </select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Assets ({inventory.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div
              className="flex items-center justify-center py-14"
              role="status"
            >
              <div className="h-9 w-9 animate-spin rounded-full border-b-2 border-blue-600" />
              <span className="ml-3 text-gray-600">Loading inventory...</span>
            </div>
          ) : inventory.length === 0 ? (
            <p className="p-10 text-center text-gray-500">
              No assets match the selected filters.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50 text-left text-gray-600">
                  <tr>
                    <th className="px-5 py-3">Asset</th>
                    <th className="px-5 py-3">Category</th>
                    <th className="px-5 py-3">Status</th>
                    <th className="px-5 py-3">Condition</th>
                    <th className="px-5 py-3">Location</th>
                    <th className="px-5 py-3">Assigned to</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {inventory.map(item => (
                    <tr key={item.id}>
                      <td className="px-5 py-4">
                        <p className="font-medium text-gray-900">{item.name}</p>
                        <p className="text-xs text-gray-500">
                          {item.asset_tag ||
                            item.serial_number ||
                            `#${item.id}`}
                        </p>
                      </td>
                      <td className="px-5 py-4">
                        {formatLabel(item.category)}
                      </td>
                      <td className="px-5 py-4">
                        <Badge variant="info">{formatLabel(item.status)}</Badge>
                      </td>
                      <td className="px-5 py-4">
                        <Badge
                          variant={item.needs_attention ? 'warning' : 'success'}
                        >
                          {formatLabel(item.condition)}
                        </Badge>
                      </td>
                      <td className="px-5 py-4">{getLocation(item)}</td>
                      <td className="px-5 py-4">
                        {item.assigned_to_user?.full_name || 'Unassigned'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default InventoryDashboard;
